import React, { useEffect, useState } from 'react';
import SmallButton from '../../../components/SmallButton';
import SubmitButton from '../../../components/SubmitButton';
import { axiosKK } from '../../../services/networkRequest';
import { VehicleDTO } from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';

interface Props {
  partnerId: number;
}

interface VehicleForm {
  name: string;
  vehicleClass: string;
  boxCapacity: string;
  weightCapacityKg: string;
  isActive: boolean;
}

const EMPTY_FORM: VehicleForm = {
  name: '',
  vehicleClass: '',
  boxCapacity: '',
  weightCapacityKg: '',
  isActive: true,
};

export default function VehiclesTab({ partnerId }: Props) {
  const [vehicles, setVehicles] = useState<VehicleDTO[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<VehicleForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const refresh = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosKK.get<{ vehicles: VehicleDTO[] }>(`/admin/partners/${partnerId}/vehicles`),
      axiosKK.get<{ classes: string[] }>(`/admin/reference/vehicle-classes`),
    ])
      .then(([v, c]) => {
        setVehicles(v.data.vehicles || []);
        setClasses(c.data.classes || []);
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditingId('new');
    setForm({
      ...EMPTY_FORM,
      vehicleClass: classes[0] || '',
    });
  };

  const openEdit = (v: VehicleDTO) => {
    setEditingId(v.id);
    setForm({
      name: v.name || '',
      vehicleClass: v.vehicleClass,
      boxCapacity: v.boxCapacity != null ? String(v.boxCapacity) : '',
      weightCapacityKg: v.weightCapacityKg != null ? String(v.weightCapacityKg) : '',
      isActive: v.isActive,
    });
  };

  const cancel = () => {
    setEditingId(null);
  };

  const submit = () => {
    if (!form.name.trim()) {
      setNetError('Name is required.');
      return;
    }
    if (!form.vehicleClass) {
      setNetError('Vehicle class is required.');
      return;
    }
    setSubmitting(true);
    setNetError(undefined);

    const payload: any = {
      name: form.name.trim(),
      vehicleClass: form.vehicleClass,
      boxCapacity: form.boxCapacity ? Number(form.boxCapacity) : null,
      weightCapacityKg: form.weightCapacityKg ? Number(form.weightCapacityKg) : null,
    };
    if (editingId !== 'new') {
      payload.isActive = form.isActive;
    }

    const request =
      editingId === 'new'
        ? axiosKK.post(`/admin/partners/${partnerId}/vehicles`, payload)
        : axiosKK.put(`/admin/partners/${partnerId}/vehicles/${editingId}`, payload);

    request
      .then(() => {
        setEditingId(null);
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const remove = (id: number) => {
    setNetError(undefined);
    axiosKK
      .delete(`/admin/partners/${partnerId}/vehicles/${id}`)
      .then(refresh)
      .catch((err) => errorMessage(setNetError)(err));
  };

  return (
    <section className="admin-partner-vehicles">
      {netError && <p className="error">{netError}</p>}

      {loading ? (
        <p>Loading vehicles…</p>
      ) : (
        <>
          <div className="pricing-save">
            <SubmitButton onClick={openCreate} disabled={editingId !== null}>
              + Add Vehicle
            </SubmitButton>
          </div>

          {editingId !== null && (
            <div className="inline-editor">
              <h4>{editingId === 'new' ? 'New vehicle' : `Edit vehicle #${editingId}`}</h4>
              <div className="inline-editor__row">
                <label>Name / Registration</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. AB12 CDE or Big White Van"
                />
              </div>
              <div className="inline-editor__row">
                <label>Class</label>
                <select
                  value={form.vehicleClass}
                  onChange={(e) => setForm((f) => ({ ...f, vehicleClass: e.target.value }))}
                >
                  <option value="">— Select class —</option>
                  {classes.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="inline-editor__row">
                <label>Capacity (boxes)</label>
                <input
                  type="number"
                  min={0}
                  value={form.boxCapacity}
                  onChange={(e) => setForm((f) => ({ ...f, boxCapacity: e.target.value }))}
                />
              </div>
              <div className="inline-editor__row">
                <label>Weight capacity (kg)</label>
                <input
                  type="number"
                  min={0}
                  value={form.weightCapacityKg}
                  onChange={(e) => setForm((f) => ({ ...f, weightCapacityKg: e.target.value }))}
                />
              </div>
              {editingId !== 'new' && (
                <div className="inline-editor__row">
                  <label>Active</label>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                </div>
              )}
              <div className="inline-editor__actions">
                <SubmitButton onClick={submit} isSubmitting={submitting} disabled={submitting}>
                  Save
                </SubmitButton>
                <SmallButton onClick={cancel}>Cancel</SmallButton>
              </div>
            </div>
          )}

          <table className="pricing-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Class</th>
                <th>Boxes</th>
                <th>Weight (kg)</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vehicles.length === 0 ? (
                <tr>
                  <td colSpan={7}>No vehicles configured.</td>
                </tr>
              ) : (
                vehicles.map((v) => (
                  <tr key={v.id}>
                    <td>{v.id}</td>
                    <td>{v.name || '—'}</td>
                    <td>{v.vehicleClass}</td>
                    <td>{v.boxCapacity ?? '—'}</td>
                    <td>{v.weightCapacityKg ?? '—'}</td>
                    <td>{v.isActive ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className="pricing-table__remove"
                        onClick={() => openEdit(v)}
                        disabled={editingId !== null}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="pricing-table__remove"
                        onClick={() => remove(v.id)}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
